import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SummaryApi from "../common";
import noimage from "../asset/noimage.png";

// Import icons from react-icons/fa (Font Awesome 5)
import { 
  FaEnvelope, 
  FaPhone, 
  FaGlobe, 
  FaMapMarkerAlt, 
  FaStar, 
  FaBriefcase, 
  FaGraduationCap, 
  FaUniversity, 
  FaFileAlt, 
  FaSpinner,
  FaUserTie
} from "react-icons/fa";

const UserInstructorDetails = () => {
  const [searchParams] = useSearchParams();
  const instructor_id = searchParams.get("instructor_id");
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!instructor_id) return;

    fetch(SummaryApi.instructordetailsById.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ instructor_id }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setDetails(data.data);
        } else {
          console.error("Failed to fetch instructor details");
        }
      })
      .catch((err) => console.error("API error:", err))
      .finally(() => setLoading(false));
  }, [instructor_id]);

  // Helper function to clean HTML skills string
  const parseSkills = (htmlString) => {
    if (!htmlString) return [];
    
    // Check for List Items first (<li>)
    if (htmlString.includes("<li")) {
      const match = htmlString.match(/<li[^>]*>(.*?)<\/li>/g);
      if (match) {
        return match.map((val) => val.replace(/<\/?li[^>]*>/g, "").trim());
      }
    }

    // Fallback: Remove all HTML tags and split by comma
    return htmlString
      .replace(/<\/?[^>]+(>|$)/g, "") // Remove tags
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <FaSpinner className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  if (!details) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <FaUserTie /> Instructor not found
        </h2>
      </div>
    );
  }

  // Handle Image URLs
  const imageUrl = details.instructor_image
    ? `${SummaryApi.instructorCourseImage.url.replace("api", "")}${details.instructor_image}`
    : noimage;

  const docUrl = details.document1
    ? `${SummaryApi.instructorCourseImage.url.replace("api", "")}${details.document1}`
    : null;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header Breadcrumb / ID */}
        <div className="flex flex-wrap justify-between items-center mb-6 gap-2">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FaUserTie className="text-blue-600" />
            Instructor Profile
          </h2>
          {/* <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">
            ID: {details.instructor_code || details.instructor_id}
          </span> */}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* LEFT COLUMN - Sidebar Card */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
              
              <div className="relative mt-8">
                <img
                  src={imageUrl}
                  alt={`${details.first_name} ${details.last_name}`}
                  className="w-32 h-32 mx-auto rounded-full border-4 border-white shadow-md object-cover bg-white"
                />
              </div>
              
              <h1 className="mt-4 text-2xl font-bold text-gray-900 capitalize">
                {details.first_name} {details.last_name}
              </h1>
              <p className="text-blue-600 font-medium">{details.job_title || "Instructor"}</p>
              
              {/* Rating */}
              <div className="flex items-center justify-center gap-1 mt-3 text-yellow-500">
                <FaStar className="w-5 h-5" />
                <span className="text-gray-700 font-bold text-lg">{details.rating || "0.0"}</span>
                <span className="text-gray-400 text-sm font-normal">/ 5.0 Rating</span>
              </div>

              {/* Contact Details */}
              <div className="mt-6 border-t border-gray-100 pt-6 space-y-4 text-left">
                <ContactItem 
                  icon={FaEnvelope} 
                  label="Email" 
                  value={details.email} 
                  isLink 
                  href={`mailto:${details.email}`} 
                />
              
                <ContactItem 
                  icon={FaGlobe} 
                  label="Website" 
                  value={details.website} 
                  isLink 
                  href={`https://${details.website}`} 
                />
                <ContactItem 
                  icon={FaMapMarkerAlt} 
                  label="Location" 
                  value={details.address} 
                />
              </div>
            </div>

            {/* Document / Certificate */}
            {/* {docUrl && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FaFileAlt className="text-blue-500" />
                  Credentials
                </h3>
                <div className="border rounded-lg p-2 bg-gray-50 group cursor-pointer overflow-hidden">
                  <img 
                    src={docUrl} 
                    alt="Document" 
                    className="w-full h-auto rounded transition-transform duration-300 group-hover:scale-105" 
                  />
                  <a 
                    href={docUrl} 
                    target="_blank" 
                    rel="noreferrer" 
                    className="block text-center mt-3 text-sm text-blue-600 font-medium hover:underline"
                  >
                    View Full Document
                  </a>
                </div>
              </div>
            )} */}
          </div>

          {/* RIGHT COLUMN - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard 
                label="Experience" 
                value={`${details.total_experience || 0} Years`} 
                icon={FaBriefcase} 
              />
              <StatCard 
                label="Qualification" 
                value={details.qualification || "N/A"} 
                icon={FaGraduationCap} 
              />
              <StatCard 
                label="University" 
                value={details.study_university || "N/A"} 
                icon={FaUniversity} 
              />
            </div>

            {/* Biography Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">About Instructor</h3>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                {details.description && <p>{details.description}</p>}
                {details.about_instructor1 && <p>{details.about_instructor1}</p>}
                {details.about_instructor2 && <p>{details.about_instructor2}</p>}
                
                {!details.description && !details.about_instructor1 && (
                  <p className="italic text-gray-400">No biography provided.</p>
                )}
              </div>
            </div>

            {/* Skills Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6 border-b pb-2">Professional Skills</h3>
              <div className="flex flex-wrap gap-3">
                {parseSkills(details.skills).length > 0 ? (
                  parseSkills(details.skills).map((skill, index) => (
                    <span 
                      key={index} 
                      className="px-4 py-3 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium border border-blue-100 hover:bg-blue-100 transition shadow-sm"
                    >
                      {skill}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-400">No skills listed.</span>
                )}
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};

/* --- Sub Components --- */

const ContactItem = ({ icon: Icon, label, value, isLink, href }) => {
  if (!value || value === "N/A") return null;
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="text-gray-400 mt-1">
        <Icon size={16} />
      </div>
      <div className="overflow-hidden w-full">
        <p className="text-xs text-gray-400 font-semibold uppercase">{label}</p>
        {isLink ? (
          <a href={href} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate block">
            {value}
          </a>
        ) : (
          <p className="text-gray-700 font-medium break-words">{value}</p>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4 transition hover:shadow-md">
    <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
      <Icon size={24} />
    </div>
    <div className="overflow-hidden">
      <p className="text-xs text-gray-500 font-semibold uppercase">{label}</p>
      <p className="text-gray-900 font-bold truncate" title={value}>{value}</p>
    </div>
  </div>
);

export default UserInstructorDetails;